import { IProgressAuditTrace } from "../../../features/progressAudit/progressAuditTracesSlice";

const EPSILON = 1e-6;

// Traces for lessons/topics are independent per item, so "previous" for
// regression purposes means the previous trace touching the *same*
// lesson/topic/course - not just the previous box in the chronological list.
export const getTraceItemKey = (trace: IProgressAuditTrace): string => {
  if (trace.branch === "lesson") {
    return `lesson:${trace.payload.lessonId}`;
  }
  if (trace.branch === "topic") {
    return `topic:${trace.payload.topicId}`;
  }
  return "course";
};

const getSnapshotProgress = (snapshot: unknown): number | undefined => {
  if (!snapshot || typeof snapshot !== "object") {
    return undefined;
  }
  const value = (snapshot as { progress?: unknown }).progress;
  return typeof value === "number" ? value : undefined;
};

// The request itself overwrote the stored progress with a lower value -
// visible within a single trace's own before/after snapshot. makeProgress
// now guards against this (SYSTEM.debt.md §15.29, service-learning PR #80:
// a lower incoming progress is ignored and flagged as
// `progressRegressionIgnored` instead of applied), so this should no
// longer fire for traces produced after that fix - kept as a safety net
// for older data and for anything the guard doesn't cover.
export const hasIntraTraceRegression = (trace: IProgressAuditTrace): boolean => {
  const before = getSnapshotProgress(trace.before);
  const after = getSnapshotProgress(trace.after);
  if (before === undefined || after === undefined) {
    return false;
  }
  return after < before - EPSILON;
};

export const findPreviousTraceForSameItem = (
  traces: IProgressAuditTrace[],
  index: number
): IProgressAuditTrace | undefined => {
  const key = getTraceItemKey(traces[index]);
  for (let i = index - 1; i >= 0; i -= 1) {
    if (getTraceItemKey(traces[i]) === key) {
      return traces[i];
    }
  }
  return undefined;
};

// The stored progress dropped between two separate requests for the same
// item - the previous request left it higher than this one found it, so
// something lowered it in between (e.g. a race between overlapping calls).
export const hasInterTraceRegression = (traces: IProgressAuditTrace[], index: number): boolean => {
  const previous = findPreviousTraceForSameItem(traces, index);
  if (!previous) {
    return false;
  }
  const previousAfter = getSnapshotProgress(previous.after);
  const trace = traces[index];
  const currentAtStart = getSnapshotProgress(trace.before) ?? getSnapshotProgress(trace.after);
  if (previousAfter === undefined || currentAtStart === undefined) {
    return false;
  }
  return currentAtStart < previousAfter - EPSILON;
};
